"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { signInUser } from "@/features/auth/api/auth-client";
import { authRepository } from "@/features/auth/api/auth-repository";
import { makeDemoCaptcha } from "@/features/auth/components/signup/signup-captcha-image";
import type { SignupScenario } from "@/features/auth/components/signup/signup-content";
import { useCaptcha } from "@/features/auth/hooks/use-captcha";
import { useSignupConfig } from "@/features/auth/hooks/use-signup-config";

/**
 * Behaviour for `/signup`. Written as a hook during the four-way design bake-off
 * so all the candidates shared one model; kept as one now that variant A is the
 * page, since it keeps the form logic out of the layout.
 *
 * The field set is the live page's exactly: nine inputs, two checkboxes, one
 * button. Two things it carries that the `AuthCard` form it replaced did not,
 * both because the reference has them:
 *   - `terms` — the "you agree to our Terms and Conditions" box, checked by
 *     default but still required, so unticking it blocks the submit.
 *   - the captcha is ALWAYS rendered. The shared `signupSchema` makes it
 *     optional (the old form hid it when `signup_captcha` was off), and the mock
 *     backend has it off — but the reference shows a security code on every
 *     visit, and parity is the brief. Hence the local schema here rather than
 *     reusing `signupSchema`, which is a ZodEffects and can't be extended.
 */

export const signupPageSchema = z
  .object({
    fname: z.string().min(2, "Enter your first name"),
    lname: z.string().min(2, "Enter your last name"),
    email: z.string().min(1, "Email is required").email("Enter a valid email"),
    username: z.string().min(3, "Use at least 3 characters"),
    phone: z.string().regex(/^[0-9+\-\s()]{6,}$/, "Enter a valid phone number"),
    password: z
      .string()
      .min(6, "Use 6–20 characters")
      .max(20, "Use 6–20 characters"),
    confirmpassword: z.string().min(1, "Confirm your password"),
    country_id: z.string().min(1, "Select your country"),
    captcha: z.string().min(1, "Enter the security code"),
    terms: z.boolean(),
    newsletter: z.boolean(),
  })
  .refine((values) => values.password === values.confirmpassword, {
    message: "Passwords don't match",
    path: ["confirmpassword"],
  })
  .refine((values) => values.terms, {
    message: "You must agree to the Terms and Conditions",
    path: ["terms"],
  });

export type SignupPageValues = z.infer<typeof signupPageSchema>;

/** Backend field names that map 1:1 onto the form, for inline 400 errors. */
const FIELD_NAMES = new Set<keyof SignupPageValues>([
  "fname",
  "lname",
  "email",
  "username",
  "phone",
  "password",
  "confirmpassword",
  "country_id",
  "captcha",
]);

/** Both checkboxes ship ticked, as on the live page. */
const BLANK: SignupPageValues = {
  fname: "",
  lname: "",
  email: "",
  username: "",
  phone: "",
  password: "",
  confirmpassword: "",
  country_id: "",
  captcha: "",
  terms: true,
  newsletter: true,
};

/** What `?scenario=populated|loading` fills in. Review data, not credentials. */
const PREFILL: SignupPageValues = {
  ...BLANK,
  fname: "Ada",
  lname: "Nwosu",
  email: "ada.nwosu@example.com",
  username: "ada_nwosu",
  phone: "+234 803 555 0142",
  password: "Passw0rd!23",
  confirmpassword: "Passw0rd!23",
  country_id: "NG",
  captcha: "K7QMX",
};

/** `?scenario=error` — a rejected submit: the two failures the live form can
 * report together, plus the summary alert above them. */
const ERROR_PREFILL: SignupPageValues = {
  ...PREFILL,
  confirmpassword: "Passw0rd!24",
  captcha: "3XZ9P",
};
export const SIGNUP_ERROR_SUMMARY =
  "We couldn't create your account. Check the highlighted fields and try again.";
const WRONG_CAPTCHA = "The security code you entered is incorrect";
const PASSWORD_MISMATCH = "Passwords don't match";

function readReferralId(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("referer_id="))
    ?.split("=")[1];
}

/* --- The shared hook ------------------------------------------------------- */

export interface SignupPageState {
  form: UseFormReturn<SignupPageValues>;
  /** Submitting — real, or forced by `?scenario=loading`. Locks the inputs. */
  submitting: boolean;
  /** The summary alert above the form, or null. */
  formError: string | null;
  /** The challenge image to render, or null until one is ready after mount. */
  captchaImage: string | null;
  /** Pull a fresh challenge (the backend consumes the token on submit). */
  refreshCaptcha: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export function useSignupPage(scenario: SignupScenario): SignupPageState {
  const router = useRouter();
  const { data: config } = useSignupConfig();
  const { captcha, refresh } = useCaptcha(true);
  const [formError, setFormError] = useState<string | null>(
    scenario === "error" ? SIGNUP_ERROR_SUMMARY : null,
  );

  const form = useForm<SignupPageValues>({
    resolver: zodResolver(signupPageSchema),
    defaultValues:
      scenario === "error"
        ? ERROR_PREFILL
        : scenario === "populated" || scenario === "loading"
          ? PREFILL
          : BLANK,
  });

  /* The seed is set after mount, never during render: the drawn fallback would
   * otherwise differ between the server and client passes and trip hydration. */
  const [seed, setSeed] = useState<number | null>(null);
  useEffect(() => {
    setSeed(Math.floor(Math.random() * 2 ** 31));
  }, []);

  const { setError } = form;
  useEffect(() => {
    if (scenario !== "error") return;
    setError("confirmpassword", { type: "manual", message: PASSWORD_MISMATCH });
    setError("captcha", { type: "manual", message: WRONG_CAPTCHA });
  }, [scenario, setError]);

  /* Prefer the real challenge; fall back to the drawn one when the backend
   * hands back nothing usable (MSW returns an empty PNG — see the fallback's
   * own note). A real data URL is always far longer than this. */
  const realImage =
    captcha?.imageDataUrl && captcha.imageDataUrl.length > 64
      ? captcha.imageDataUrl
      : null;
  const demoImage = useMemo(
    () => (seed === null ? null : makeDemoCaptcha(seed).dataUrl),
    [seed],
  );

  function refreshCaptcha() {
    // Step the seed deterministically rather than re-rolling — one call, one
    // new challenge, and no second source of randomness.
    setSeed((current) =>
      current === null ? 1 : (current * 1664525 + 1013904223) % 2 ** 31,
    );
    void refresh();
  }

  async function submit(values: SignupPageValues) {
    setFormError(null);

    // `terms` is a client-side gate only — the backend payload has no field for
    // it, so it's dropped rather than sent.
    const { terms, ...payload } = values;
    void terms;

    const result = await authRepository.signup({
      ...payload,
      captchasolution: captcha?.token,
      ref_id: readReferralId(),
    });

    if (result.ok) {
      if (config?.accountActivation) {
        router.push("/signup/activate");
        return;
      }
      const signedIn = await signInUser(
        {
          username: values.username.trim(),
          password: values.password,
          rememberMe: true,
        },
        "/",
      );
      router.push(
        signedIn.url && !signedIn.url.includes("/login") ? signedIn.url : "/",
      );
      router.refresh();
      return;
    }

    // The challenge is single-use, so a rejected submit needs a fresh one.
    refreshCaptcha();

    if ("fieldErrors" in result) {
      for (const { field, message } of result.fieldErrors) {
        if (FIELD_NAMES.has(field as keyof SignupPageValues)) {
          form.setError(field as keyof SignupPageValues, {
            type: "manual",
            message,
          });
        } else {
          setFormError(message);
        }
      }
    } else {
      setFormError(result.formError);
    }
  }

  return {
    form,
    submitting: scenario === "loading" || form.formState.isSubmitting,
    formError,
    captchaImage: realImage ?? demoImage,
    refreshCaptcha,
    onSubmit: form.handleSubmit(submit),
  };
}
